import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProjectGroup } from '@/services/backend/IBackend';
import { getBackend } from '@/services/backend';
import { logger } from '@/lib/logger';

/**
 * Project groups ("Teams" in the UI) — the user-defined org grouping layer above
 * projects. Definitions live in the core DB (`group.*` IPC); this store mirrors
 * them client-side and is cached to localStorage so the grouped project lists
 * render instantly on load (the backend is the source of truth and overwrites
 * the cache on `loadGroups`). On a remote/mobile client the group commands are
 * forwarded to the host, so teams defined on the desktop show up on the phone.
 */
interface GroupState {
  groups: ProjectGroup[];
  isLoading: boolean;
  error: string | null;

  loadGroups: () => Promise<void>;
  /** Create a new team; appended after the current max order. Returns it. */
  createGroup: (name: string, color?: string | null) => Promise<ProjectGroup>;
  /** Rename / recolor an existing team. */
  updateGroup: (id: string, patch: { name?: string; color?: string | null }) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  /** Persist a new display order (full list of ids in the desired order). */
  reorderGroups: (ids: string[]) => Promise<void>;
}

export const useGroupStore = create<GroupState>()(
  persist(
    (set, get) => ({
      groups: [],
      isLoading: false,
      error: null,

      loadGroups: async () => {
        set({ isLoading: true, error: null });
        try {
          const groups = await getBackend().groups.list();
          set({ groups, isLoading: false });
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Failed to load teams';
          logger.error('GroupStore', 'Failed to load teams:', error);
          set({ error: msg, isLoading: false });
        }
      },

      createGroup: async (name, color = null) => {
        const id =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `grp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const saved = await getBackend().groups.upsert({ id, name, color });
        set((s) => ({ groups: [...s.groups, saved].sort(byOrder) }));
        return saved;
      },

      updateGroup: async (id, patch) => {
        const existing = get().groups.find((g) => g.id === id);
        if (!existing) throw new Error(`Unknown team ${id}`);
        const saved = await getBackend().groups.upsert({
          id,
          name: patch.name ?? existing.name,
          color: patch.color !== undefined ? patch.color : existing.color,
          sortOrder: existing.sortOrder,
        });
        set((s) => ({ groups: s.groups.map((g) => (g.id === id ? saved : g)).sort(byOrder) }));
      },

      deleteGroup: async (id) => {
        await getBackend().groups.remove(id);
        set((s) => ({ groups: s.groups.filter((g) => g.id !== id) }));
      },

      reorderGroups: async (ids) => {
        // Optimistic: reorder locally, then persist. The next loadGroups
        // reconciles against the backend if the write failed.
        set((s) => {
          const index = new Map(ids.map((id, i) => [id, i]));
          return {
            groups: [...s.groups].sort(
              (a, b) => (index.get(a.id) ?? a.sortOrder) - (index.get(b.id) ?? b.sortOrder),
            ),
          };
        });
        try {
          await getBackend().groups.reorder(ids);
        } catch (error) {
          logger.error('GroupStore', 'Failed to reorder teams:', error);
          void get().loadGroups();
          throw error;
        }
      },
    }),
    {
      name: 'ateliercode-groups',
      partialize: (state) => ({ groups: state.groups }),
    },
  ),
);

function byOrder(a: ProjectGroup, b: ProjectGroup): number {
  return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
}
